import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { getPlatformConfig, normalizePlatform } from '../../../shared/config/platformConfig';
import { checkCustomerStatus } from '../../customers/services/customersApi';
import { useAppDispatch, useAppSelector } from '../../../shared/store/hooks';
import { settingsSliceAPI } from '../../settings/store/settingsSlice';
import { api } from '../../../shared/services/api/client';
import { useAuth } from '../../auth/hooks/useAuth';
import { useMerchantAuth } from '../../../contexts/AuthContext';
import {
    setEmail,
    setPhoneNumber,
    setName,
    setBirthMonth,
    setBirthDay,
    setPlatform,
    setOptInMarketing,
    setErrors,
    setQr,
    setFormQr,
    setMerchantId,
    setStatusMessage,
    setEmailSent,
    resetGenerate,
} from '../store/generateSlice';
import { validateEmail, validatePhone, validateName } from '../../../shared/utils/validators';
import { normalizeEmail } from '../../../shared/utils/stringUtils';
import { formatMonthName } from '../../../shared/utils/dateFormatters';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';
import { TierLimitWarningModal } from '../../../shared/components/TierLimitWarningModal';
import { createLogger } from '../../../shared/config/logger';
import {
    renderWalletQrIntoWindow,
    openWalletQrLoadingTab,
    closeWalletQrTab,
    focusMerchantAppTab,
    openWalletQrInNewTab,
    MAX_QR_STRING_LENGTH,
} from '../utils/walletQrWindow';

const logger = createLogger('GeneratePage');

/** Raw URL under QR codes — only in dev/test builds (hidden in production). */
const SHOW_QR_ENCODED_URL_DEBUG = process.env.NODE_ENV !== 'production';

export default function Generate() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const {
        email,
        phone_number,
        name,
        birthMonth,
        birthDay,
        platform,
        optInMarketing,
        errors,
        qr,
        formQr,
        statusMessage,
    } = useAppSelector((state) => state.generate);

    const [emailChecked, setEmailChecked] = useState(false);
    const [checkingEmail, setCheckingEmail] = useState(false);
    const [customerStatus, setCustomerStatus] = useState(null); // 'hasPass', 'exists', 'new', or null
    const [customerStatusMessage, setCustomerStatusMessage] = useState('');
    const [generatingPass, setGeneratingPass] = useState(false);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [warningMessage, setWarningMessage] = useState('');
    const {
        state: { userId },
    } = useAuth();
    const merchantAuth = useMerchantAuth();

    // Effective merchant ID: for staff use merchant from getMe, for owner use session userId (= merchant id)
    const effectiveMerchantId = merchantAuth?.user?.merchantId ?? merchantAuth?.user?.id ?? userId;

    const generalsettings = useAppSelector((state) => state.settings.generalsettings);
    const generalSettingsFetchStatus = useAppSelector((state) => state.settings.generalSettingsFetchStatus);
    const settingsLoaded = generalSettingsFetchStatus === 'succeeded';
    const settingsLoading =
        generalSettingsFetchStatus === 'idle' || generalSettingsFetchStatus === 'loading';
    const settingsFailed = generalSettingsFetchStatus === 'failed';
    const normalizedSelectedPlatform = platform ? normalizePlatform(platform) : null;
    const needsAppleTemplate =
        settingsLoaded && normalizedSelectedPlatform === 'ios' && !generalsettings?.apple_class_id;
    const needsGoogleTemplate =
        settingsLoaded && normalizedSelectedPlatform === 'android' && !generalsettings?.google_class_id;
    const passTemplateBlocksGenerate = needsAppleTemplate || needsGoogleTemplate;
    const settingsStateBlocksGenerate = settingsLoading || settingsFailed;

    useEffect(() => {
        if (effectiveMerchantId) {
            dispatch(setMerchantId(effectiveMerchantId));
        }
    }, [dispatch, effectiveMerchantId]);

    useEffect(() => {
        if (!effectiveMerchantId) {
            return;
        }
        if (generalSettingsFetchStatus === 'idle') {
            dispatch(settingsSliceAPI.fetchGeneralSettings());
        }
    }, [dispatch, effectiveMerchantId, generalSettingsFetchStatus]);

    const [formQrLoading, setFormQrLoading] = useState(false);
    const [formQrError, setFormQrError] = useState(null);
    const [showCustomerFormQr, setShowCustomerFormQr] = useState(false);
    /** Pass add URL behind the wallet QR (for local testing; remove when no longer needed). */
    const [walletPassQrUrl, setWalletPassQrUrl] = useState(null);
    const [walletQrTabBlocked, setWalletQrTabBlocked] = useState(false);

    useEffect(() => {
        if (!effectiveMerchantId) {
            setFormQrLoading(false);
            return undefined;
        }

        let cancelled = false;
        setFormQrLoading(true);
        setFormQrError(null);
        setShowCustomerFormQr(false);

        (async () => {
            try {
                const res = await api.get('/merchant-signup-link');
                const url = res.data?.url;
                if (cancelled) {
                    return;
                }
                if (!url) {
                    logger.error('merchant-signup-link missing url', { responseData: res.data });
                    setFormQrError('Could not load your customer form link.');
                    return;
                }
                dispatch(setFormQr(url));
            } catch (err) {
                if (!cancelled) {
                    logger.error('Failed to fetch customer form link', {
                        status: err?.response?.status,
                        data: err?.response?.data || err.message,
                    });
                    setFormQrError(
                        extractErrorMessage(err) || 'Failed to load your customer form link.'
                    );
                }
            } finally {
                if (!cancelled) {
                    setFormQrLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [dispatch, effectiveMerchantId]);

    const handleEmailCheck = async () => {
        if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
            return;
        }

        // Clear previous pass result when checking (new or same) email
        dispatch(setQr(null));
        setWalletPassQrUrl(null);
        dispatch(setStatusMessage(null));
        dispatch(setEmailSent(null));

        setCheckingEmail(true);
        try {
            const lowerCaseEmail = normalizeEmail(email);
            const res = await checkCustomerStatus(lowerCaseEmail);

            // Customer exists or is new
            setCustomerStatus(res.data.status);
            setCustomerStatusMessage(res.data.message || '');
            setEmailChecked(true);

            if (res.data.status === 'alreadySignedUp') {
                setCustomerStatusMessage(res.data.message || 'This customer already signed up with your merchant.');
                return;
            }

            dispatch(setErrors({}));
            if (res.data.status === 'hasPass') {
                dispatch(
                    setErrors({
                        email:
                            res.data.message ||
                            'This customer already has a pass with your business.',
                    })
                );
            } else {
                dispatch(setErrors({}));
            }

            if (res.data.status === 'exists' && res.data.customer) {
                // Pre-fill form with existing customer data
                const customer = res.data.customer;
                dispatch(setName(customer.name || ''));
                dispatch(setPhoneNumber(customer.phone || ''));
                if (customer.birthdayMonth) {
                    dispatch(
                        setBirthMonth(customer.birthdayMonth)
                    );
                }
                if (customer.birthdayDay) {
                    dispatch(setBirthDay(customer.birthdayDay));
                }
                dispatch(setOptInMarketing(customer.marketing_opt_in || false));

                // Show info message about existing birthday
                if (customer.birthdayMonth && customer.birthdayDay) {
                    const monthName = formatMonthName(customer.birthdayMonth);
                    dispatch(
                        setErrors({
                            birthday: `This customer already exists in our database. Their existing birthday (${monthName} ${parseInt(customer.birthdayDay)}) will be used for this pass.`,
                        })
                    );
                }
            } else if (res.data.status === 'new') {
                setCustomerStatusMessage('');
                // Clear form for new customer
                dispatch(setName(''));
                dispatch(setPhoneNumber(''));
                dispatch(setBirthMonth(''));
                dispatch(setBirthDay(''));
                dispatch(setOptInMarketing(false));
            }
        } catch (err) {
            // Handle 400 response (customer already has a pass) as a valid response
            if (err.response && err.response.status === 400 && err.response.data) {
                const errorData = err.response.data;
                // Check if this is the "hasPass" status or just a generic 400
                if (errorData.status === 'hasPass' || errorData.status === 'alreadySignedUp' || errorData.message) {
                    setCustomerStatus(errorData.status || 'hasPass');
                    dispatch(
                        setErrors({
                            email:
                                errorData.message ||
                                (errorData.status === 'alreadySignedUp'
                                    ? 'This customer already signed up with your merchant.'
                                    : 'This customer already has a pass with your business.'),
                        })
                    );
                    setEmailChecked(false);
                } else {
                    // Generic 400 error
                    logger.error('Error checking customer status', { error: err, status: err.response?.status });
                    dispatch(
                        setErrors({
                            email:
                                errorData.message ||
                                'Error checking customer status. Please try again.',
                        })
                    );
                    setEmailChecked(false);
                }
            } else {
                // Network or other error
                logger.error('Error checking customer status', { error: err });
                dispatch(setErrors({ email: 'Error checking customer status. Please try again.' }));
                setEmailChecked(false);
            }
        } finally {
            setCheckingEmail(false);
        }
    };

    const handleGenerate = async (e) => {
        e.preventDefault();

        if (!emailChecked) {
            dispatch(setErrors({ email: 'Please check the email first.' }));
            return;
        }

        if (customerStatus === 'hasPass' || customerStatus === 'alreadySignedUp') {
            return;
        }

        const newErrors = {};

        const nameValidation = validateName(name);
        if (!nameValidation.valid) {
            newErrors.name = nameValidation.error;
        }

        const emailValidation = validateEmail(email);
        if (!emailValidation.valid) {
            newErrors.email = emailValidation.error;
        }

        const phoneValidation = validatePhone(phone_number);
        if (!phoneValidation.valid) {
            newErrors.phone_number = phoneValidation.error;
        }

        if (!platform) {
            newErrors.platform = 'Platform is required.';
        }

        if (Object.keys(newErrors).length > 0) {
            dispatch(setErrors(newErrors));
            return;
        }

        if (settingsLoading) {
            dispatch(
                setErrors({
                    general: 'Loading merchant settings. Please wait a moment and try again.',
                })
            );
            return;
        }

        if (settingsFailed) {
            dispatch(
                setErrors({
                    general:
                        'Could not load merchant settings. Refresh the page and try again before generating a pass.',
                })
            );
            return;
        }

        if (settingsLoaded) {
            const np = normalizePlatform(platform);
            if (np === 'ios' && !generalsettings?.apple_class_id) {
                dispatch(
                    setErrors({
                        general:
                            'Create your Apple Pass template in Settings before generating iOS passes.',
                    })
                );
                return;
            }
            if (np === 'android' && !generalsettings?.google_class_id) {
                dispatch(
                    setErrors({
                        general:
                            'Create your Android Pass template in Settings before generating Google Wallet passes.',
                    })
                );
                return;
            }
        }

        dispatch(setErrors({}));
        setGeneratingPass(true);
        setWalletQrTabBlocked(false);

        const walletQrTab = openWalletQrLoadingTab();
        setWalletQrTabBlocked(!walletQrTab);

        try {
            const lowerCaseEmail = normalizeEmail(email);
            const birthDate = birthMonth && birthDay ? `2000-${birthMonth}-${birthDay}` : null;

            const platformConfig = getPlatformConfig(platform);
            if (!platformConfig) {
                throw new Error('Unsupported platform');
            }

            const res = await platformConfig.passCreatorQR(
                lowerCaseEmail,
                phone_number,
                name,
                birthDate,
                platform,
                optInMarketing
            );

            // Handle platform-specific response handling
            const normalizedPlatform = normalizePlatform(platform);
            const emailSent = res.data.emailSent ?? null;
            const emailFailed = emailSent && emailSent.ok === false;

            dispatch(setEmailSent(emailSent));

            let qrRenderOpts = null;

            if (normalizedPlatform === 'ios') {
                setWalletPassQrUrl(res.data.passUrl || null);
                const qrValue = res.data.qrCode || res.data.passUrl;
                dispatch(setQr(qrValue));
                const message = emailFailed
                    ? 'The customer pass was created. A new tab should show a QR code for the customer to add the pass to Apple Wallet. The confirmation email could not be sent.'
                    : res.data.message ||
                      'An email has been sent with the customer\'s Apple Wallet pass. A new tab also has a QR code the customer can scan to add the pass.';
                dispatch(setStatusMessage(message));
                qrRenderOpts = {
                    qr: qrValue,
                    passAddUrl: res.data.passUrl || null,
                    heading: 'Scan to add — Apple Wallet',
                };
            } else if (normalizedPlatform === 'android') {
                setWalletPassQrUrl(res.data.passUrl || null);
                const fromApi = res.data.qrCode || null;
                const fallback = res.data.passUrl || null;
                const safeFallback =
                    typeof fallback === 'string' && fallback.length <= MAX_QR_STRING_LENGTH
                        ? fallback
                        : null;
                const qrValue = fromApi || safeFallback;
                dispatch(setQr(qrValue));
                const message = emailFailed
                    ? 'The customer pass was created. A new tab should show a QR code for the customer to add the pass to Google Wallet. The confirmation email could not be sent.'
                    : res.data.message ||
                      'An email has been sent with the customer\'s Google Wallet pass. A new tab also has a QR code the customer can scan to add the pass.';
                dispatch(setStatusMessage(message));
                qrRenderOpts = {
                    qr: qrValue,
                    passAddUrl: res.data.passUrl || null,
                    heading: 'Scan to add — Google Wallet',
                };
            }

            if (qrRenderOpts) {
                renderWalletQrIntoWindow(walletQrTab, qrRenderOpts);
                focusMerchantAppTab();
            } else {
                closeWalletQrTab(walletQrTab);
            }
        } catch (err) {
            closeWalletQrTab(walletQrTab);
            // Extract error message from API response
            const errorMessage = extractErrorMessage(err);

            // Check if it's a tier limit error
            if (errorMessage.includes('limit exceeded') || errorMessage.includes('Please upgrade')) {
                setWarningMessage(errorMessage);
                setShowWarningModal(true);
                dispatch(setErrors({}));
            } else {
                dispatch(
                    setErrors({
                        general: errorMessage,
                    })
                );
            }
        } finally {
            setGeneratingPass(false);
        }
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-primary mb-6">Generate Customer Pass</h2>
            <div className="flex flex-col md:flex-row md:space-x-6 space-y-6 md:space-y-0">
                {/* Form Panel */}
                <form
                    onSubmit={handleGenerate}
                    className="flex-1 space-y-4 bg-white shadow-md rounded-xl p-6"
                >
                    <h2 className="text-2xl font-bold text-primary">Create Loyalty Pass</h2>

                    {emailChecked &&
                        customerStatus !== 'hasPass' &&
                        customerStatus !== 'alreadySignedUp' &&
                        passTemplateBlocksGenerate && (
                        <div
                            className="mb-4 text-amber-900 text-sm bg-amber-50 border border-amber-200 rounded-lg px-4 py-3"
                            role="alert"
                        >
                            {needsAppleTemplate && (
                                <p>
                                    Create your{' '}
                                    <Link
                                        to="/dashboard/settings/apple-pass"
                                        className="text-primary font-medium underline"
                                    >
                                        Apple Pass template
                                    </Link>{' '}
                                    before generating passes for iPhone.
                                </p>
                            )}
                            {needsGoogleTemplate && (
                                <p>
                                    Create your{' '}
                                    <Link
                                        to="/dashboard/settings/android-pass"
                                        className="text-primary font-medium underline"
                                    >
                                        Android Pass template
                                    </Link>{' '}
                                    before generating Google Wallet passes.
                                </p>
                            )}
                        </div>
                    )}

                    {/* Email Input - Always Visible */}
                    <div>
                        <label className="block font-medium mb-1">
                            Email <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="email"
                                className="flex-1 p-3 border border-gray-300 rounded-md"
                                placeholder="john.doe@example.com"
                                value={email}
                                onChange={(e) => {
                                    dispatch(setEmail(e.target.value));
                                    setEmailChecked(false);
                                    setCustomerStatus(null);
                                    setCustomerStatusMessage('');
                                    dispatch(setErrors({}));
                                    dispatch(setStatusMessage(null));
                                    dispatch(setQr(null));
                                    setWalletPassQrUrl(null);
                                    setWalletQrTabBlocked(false);
                                    dispatch(setEmailSent(null));
                                }}
                                onBlur={handleEmailCheck}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleEmailCheck();
                                    }
                                }}
                                disabled={checkingEmail}
                            />
                            <button
                                type="button"
                                onClick={handleEmailCheck}
                                disabled={
                                    checkingEmail || !email.trim() || !/\S+@\S+\.\S+/.test(email)
                                }
                                className="px-4 py-3 bg-primary text-white rounded-md hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {checkingEmail ? 'Checking...' : 'Check'}
                            </button>
                        </div>
                        {errors.email && customerStatus !== 'hasPass' && (
                            <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                        )}
                        {(customerStatus === 'hasPass' || customerStatus === 'alreadySignedUp') && (
                            <p className="text-amber-700 text-sm mt-1 bg-amber-50 p-2 rounded border border-amber-200">
                                {customerStatus === 'alreadySignedUp'
                                    ? (customerStatusMessage || 'This customer already signed up with your merchant.')
                                    : (customerStatusMessage || 'This customer already has a pass with your business.')}
                            </p>
                        )}
                        {customerStatus === 'exists' && (
                            <p className="text-blue-600 text-sm mt-1 bg-blue-50 p-2 rounded">
                                {customerStatusMessage || 'This customer exists in our database. Their information has been pre-filled.'}
                            </p>
                        )}
                        {customerStatus === 'new' && (
                            <p className="text-green-600 text-sm mt-1 bg-green-50 p-2 rounded">
                                This is a new customer. Please fill in their information.
                            </p>
                        )}
                        {customerStatus === 'hasPass' && (
                            <p className="text-amber-800 text-sm mt-1 bg-amber-50 p-2 rounded border border-amber-200">
                                {errors.email ||
                                    'This customer already has a pass with your business.'}
                            </p>
                        )}
                    </div>

                    {/* Rest of form - only show after email is checked and customer doesn't have a pass or already signed up */}
                    {emailChecked && customerStatus !== 'hasPass' && customerStatus !== 'alreadySignedUp' && (
                        <>
                            <div>
                                <label className="block font-medium mb-1">
                                    Full Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`w-full p-3 border border-gray-300 rounded-md ${customerStatus === 'exists' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    placeholder="John Doe"
                                    value={name}
                                    onChange={(e) =>
                                        dispatch(setName(e.target.value))
                                    }
                                    disabled={customerStatus === 'exists'}
                                />
                                {errors.name && (
                                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                                )}
                                {customerStatus === 'exists' && name && (
                                    <p className="text-gray-600 text-xs mt-1 italic">
                                        Name is locked to existing customer data
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block font-medium mb-1">Phone #</label>
                                <input
                                    type="tel"
                                    inputMode="numeric"
                                    className={`w-full p-3 border border-gray-300 rounded-md ${customerStatus === 'exists' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    placeholder="1234567890"
                                    value={phone_number}
                                    maxLength={10}
                                    onChange={(e) => {
                                        const value = e.target.value
                                            .replace(/\D/g, '')
                                            .slice(0, 10);
                                        dispatch(setPhoneNumber(value));
                                    }}
                                    disabled={customerStatus === 'exists'}
                                />
                                {errors.phone_number && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {errors.phone_number}
                                    </p>
                                )}
                                {customerStatus === 'exists' && phone_number && (
                                    <p className="text-gray-600 text-xs mt-1 italic">
                                        Phone number is locked to existing customer data
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block font-medium mb-1">Birthday:</label>
                                <div className="flex space-x-2">
                                    <select
                                        value={birthMonth}
                                        onChange={(e) =>
                                            dispatch(setBirthMonth(e.target.value))
                                        }
                                        className={`w-1/2 p-3 border border-gray-300 rounded-md ${customerStatus === 'exists' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        disabled={customerStatus === 'exists'}
                                    >
                                        <option value="">Month</option>
                                        {Array.from({ length: 12 }, (_, i) => (
                                            <option
                                                key={i + 1}
                                                value={String(i + 1).padStart(2, '0')}
                                            >
                                                {new Date(0, i).toLocaleString('default', {
                                                    month: 'long',
                                                })}
                                            </option>
                                        ))}
                                    </select>
                                    <select
                                        value={birthDay}
                                        onChange={(e) =>
                                            dispatch(setBirthDay(e.target.value))
                                        }
                                        className={`w-1/2 p-3 border border-gray-300 rounded-md ${customerStatus === 'exists' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        disabled={customerStatus === 'exists'}
                                    >
                                        <option value="">Day</option>
                                        {Array.from({ length: 31 }, (_, i) => (
                                            <option
                                                key={i + 1}
                                                value={String(i + 1).padStart(2, '0')}
                                            >
                                                {i + 1}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {errors.birthday && (
                                    <p className="text-red-500 text-sm mt-1">{errors.birthday}</p>
                                )}
                                {customerStatus === 'exists' && birthMonth && birthDay && (
                                    <p className="text-gray-600 text-xs mt-1 italic">
                                        Birthday is locked to existing customer data
                                    </p>
                                )}
                            </div>

                            {/* Platform Selector */}
                            <div>
                                <label className="block font-medium mb-1">Platform:</label>
                                <select
                                    value={platform}
                                    onChange={(e) =>
                                        dispatch(setPlatform(e.target.value))
                                    }
                                    className="w-full p-3 border border-gray-300 rounded-md"
                                >
                                    <option value="">Select platform</option>
                                    <option value="iphone">iPhone</option>
                                    <option value="android">Android</option>
                                </select>
                                {errors.platform && (
                                    <p className="text-red-500 text-sm mt-1">{errors.platform}</p>
                                )}
                                {settingsLoading && (
                                    <p className="text-gray-600 text-sm mt-1">
                                        Loading merchant settings...
                                    </p>
                                )}
                                {settingsFailed && (
                                    <p className="text-amber-800 text-sm mt-1">
                                        Could not verify template settings. Refresh and try again.
                                    </p>
                                )}
                                {passTemplateBlocksGenerate && (
                                    <p className="text-amber-800 text-sm mt-1">
                                        Pass generation is disabled until the template for this
                                        platform is created in Settings.
                                    </p>
                                )}
                            </div>

                            {/* Marketing Opt-in Checkbox */}
                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="optInMarketing"
                                    checked={optInMarketing}
                                    onChange={(e) =>
                                        dispatch(setOptInMarketing(e.target.checked))
                                    }
                                    className="w-5 h-5"
                                />
                                <label htmlFor="optInMarketing" className="text-sm text-gray-700">
                                    I agree to receive marketing messages.{' '}
                                    <span className="text-red-500">*</span>
                                </label>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    type="submit"
                                    className="w-full bg-primary text-white py-3 rounded-md hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2 min-h-[48px]"
                                    disabled={
                                        !name.trim() ||
                                        !email.trim() ||
                                        !optInMarketing ||
                                        generatingPass ||
                                        settingsStateBlocksGenerate ||
                                        passTemplateBlocksGenerate
                                    }
                                >
                                    {generatingPass ? (
                                        <>
                                            <svg
                                                className="animate-spin h-5 w-5 text-white"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                />
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                />
                                            </svg>
                                            <span>Generating pass...</span>
                                        </>
                                    ) : (
                                        normalizePlatform(platform) === 'ios' ? 'Generate QR Code' : 'Generate Pass'
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className="w-full bg-gray-300 text-gray-800 py-3 rounded-md hover:bg-gray-400 transition"
                                    onClick={() => {
                                        dispatch(resetGenerate());
                                        setWalletPassQrUrl(null);
                                        setWalletQrTabBlocked(false);
                                        setEmailChecked(false);
                                        setCustomerStatus(null);
                                    }}
                                >
                                    Clear
                                </button>
                            </div>
                            {errors.general && (
                                <div className="mt-2">
                                    <p className="text-red-500 text-sm mb-2">{errors.general}</p>
                                    {(errors.general.includes('limit exceeded') || errors.general.includes('Please upgrade')) && (
                                        <button
                                            type="button"
                                            onClick={() => navigate('/dashboard/settings/membership')}
                                            className="mt-2 bg-primary text-white px-4 py-2 rounded-md hover:bg-blue-700 transition text-sm font-medium"
                                        >
                                            Upgrade Plan
                                        </button>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    {qr && walletQrTabBlocked && (
                        <div className="mt-6 p-4 border border-amber-300 bg-amber-50 rounded-md">
                            <p className="text-sm text-amber-900 mb-3">
                                Your browser blocked the QR window. Allow popups for this site or open the QR code
                                manually.
                            </p>
                            <button
                                type="button"
                                className="w-full bg-primary text-white py-2 rounded-md hover:bg-blue-700 transition text-sm font-medium"
                                onClick={() => {
                                    openWalletQrInNewTab({
                                        qr,
                                        passAddUrl: walletPassQrUrl,
                                        heading:
                                            normalizePlatform(platform) === 'ios'
                                                ? 'Scan to add — Apple Wallet'
                                                : 'Scan to add — Google Wallet',
                                    });
                                }}
                            >
                                Open QR code
                            </button>
                        </div>
                    )}
                    {qr && !walletQrTabBlocked && (
                        <div className="mt-4">
                            <button
                                type="button"
                                className="text-sm text-primary font-medium hover:underline"
                                onClick={() => {
                                    openWalletQrInNewTab({
                                        qr,
                                        passAddUrl: walletPassQrUrl,
                                        heading:
                                            normalizePlatform(platform) === 'ios'
                                                ? 'Scan to add — Apple Wallet'
                                                : 'Scan to add — Google Wallet',
                                    });
                                }}
                            >
                                Open QR code in new tab again
                            </button>
                            {SHOW_QR_ENCODED_URL_DEBUG &&
                                (walletPassQrUrl || (!qr.startsWith('data:') && qr)) && (
                                    <div className="mt-3 p-3 rounded-md border border-dashed border-amber-300 bg-amber-50 text-sm">
                                        <p className="font-medium text-amber-900 mb-1">
                                            Testing: URL encoded in this QR
                                        </p>
                                        <a
                                            href={walletPassQrUrl || qr}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary break-all underline"
                                        >
                                            {walletPassQrUrl || qr}
                                        </a>
                                    </div>
                                )}
                        </div>
                    )}
                    {statusMessage && (
                        <div className="mt-6 p-4 border border-blue-300 bg-blue-50 rounded-md">
                            <h3 className="text-lg font-semibold mb-2 text-blue-700">
                                Pass Generated
                            </h3>
                            <p className="text-blue-800">{statusMessage}</p>
                        </div>
                    )}
                </form>

                {/* QR Code Panel */}
                <div className="flex-1 space-y-4 bg-white shadow-md rounded-xl p-6">
                    <h2 className="text-2xl font-bold text-primary">Customer Form QR Code</h2>
                    <p className="text-gray-600">
                        Customers can scan this QR code to access a form where they can input their
                        information.
                    </p>

                    {formQrLoading && (
                        <p className="text-gray-600 text-sm">Loading your customer form link…</p>
                    )}
                    {formQrError && !formQrLoading && (
                        <p className="text-red-600 text-sm">{formQrError}</p>
                    )}
                    {SHOW_QR_ENCODED_URL_DEBUG && !formQrLoading && !formQrError && formQr && (
                        <div className="p-3 rounded-md border border-dashed border-amber-300 bg-amber-50 text-sm">
                            <p className="font-medium text-amber-900 mb-1">
                                Testing: URL encoded in this QR
                            </p>
                            <a
                                href={formQr}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary break-all underline"
                            >
                                {formQr}
                            </a>
                        </div>
                    )}
                    {!formQrLoading && !formQrError && formQr && !showCustomerFormQr && (
                        <button
                            type="button"
                            className="w-full bg-primary text-white py-3 rounded-md hover:bg-blue-700 transition"
                            onClick={() => {
                                setShowCustomerFormQr(true);
                            }}
                        >
                            Show customer form QR
                        </button>
                    )}
                    {!formQrLoading && !formQrError && formQr && showCustomerFormQr && (
                        <div className="mt-4 flex flex-col items-center">
                            <div className="border p-4 bg-white inline-block">
                                <QRCodeSVG value={formQr} size={200} />
                            </div>
                            <p className="mt-2 text-sm text-gray-500">
                                Scan this QR code to access the customer registration form.
                            </p>
                            <button
                                type="button"
                                className="mt-4 text-sm text-primary hover:underline"
                                onClick={() => {
                                    setShowCustomerFormQr(false);
                                }}
                            >
                                Hide QR
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <TierLimitWarningModal
                isOpen={showWarningModal}
                onClose={() => setShowWarningModal(false)}
                message={warningMessage}
            />
        </div>
    );
}
